/*
 * The MocTel Vox core for browsers: the mt-vox command/event API (docs/api.md
 * in mt-vox) on top of a SipStack. Same guarantees as the native core:
 *
 * - one active call: dialing, answering or resuming a call holds the others;
 * - the focus call is the one last dialed, answered or resumed; another call
 *   that connects while the focus call is live is held at once;
 * - on a focus change, calls that are not connected yet (ringing, early
 *   media) are silenced locally; any resume lifts it;
 * - hold is reconciled: the latest wish wins even while a re-INVITE is in
 *   flight;
 * - an incoming REFER is followed (blind transfer).
 */
import { Conference } from "./conference";
import { callStats, MosInput, mosEstimate } from "./stats";
import type {
  CallListener,
  RegState,
  SipStack,
  StackAccount,
  StackCall,
  StackCallState,
} from "./sip";
import {
  AccountConfig,
  CallInfo,
  CallState,
  Command,
  CommandError,
  CoreEvent,
  CoreSettings,
  EventHandler,
  Json,
} from "./types";

export const API_VERSION = 0;
export const CORE_VERSION = "0.1.0";

interface AccountRec {
  cfg: AccountConfig;
  stack: StackAccount;
  registered: boolean;
  last?: string;
}

export interface CallRec {
  id: string;
  account: string;
  stack: StackCall;
  dir: "in" | "out";
  state: CallState;
  started?: number;
  onHold: boolean;
  wantHold: boolean;
  holdBusy: boolean;
  remoteHold: boolean;
  silenced: boolean;
  muted: boolean;
  transferredFrom?: string;
  /** the dialer's own reference (a booking...), sent as X-MT-Ref */
  ref?: string;
}

const DIGITS = /^[0-9*#A-Da-d]+$/;
const UNSAFE = /[<>"\r\n ]/;
/** a call.dial ref: printable, no control characters, short */
const REF = /^[^\x00-\x1f\x7f]{1,128}$/;
export const REF_HEADER = "X-MT-Ref";

/**
 * A command family handled outside the call core (provisioning, settings,
 * audio devices, diagnostics). Returns undefined for commands it does not
 * know, so the next module (or "unknown_command") gets them.
 */
export interface CoreModule {
  command(c: Command): Promise<Json | void> | undefined;
  stop?(): Promise<void> | void;
}

export class Core {
  private modules: CoreModule[] = [];
  private accounts = new Map<string, AccountRec>();
  private calls = new Map<string, CallRec>();
  private focusId = "";
  private dnd = false;
  private statsTimer?: ReturnType<typeof setInterval>;
  private nextAutoId = 1_000_000;
  readonly conference: Conference;

  constructor(
    private stack: SipStack,
    private settings: CoreSettings,
    private emit: EventHandler,
  ) {
    this.conference = new Conference(this);
  }

  start(): void {
    this.event("core.ready", {
      version: CORE_VERSION,
      api: API_VERSION,
      platform: "web",
    });
    const every = this.settings.stats_interval ?? 0;
    if (every > 0) {
      this.statsTimer = setInterval(() => void this.emitStats(), every * 1000);
    }
  }

  use(m: CoreModule): void {
    this.modules.push(m);
  }

  async stop(): Promise<void> {
    if (this.statsTimer) clearInterval(this.statsTimer);
    for (const m of this.modules) await m.stop?.();
    for (const c of this.calls.values()) {
      await c.stack.hangup().catch(() => undefined);
    }
    for (const a of this.accounts.values()) {
      await a.stack.dispose().catch(() => undefined);
    }
    this.accounts.clear();
  }

  /** Run one command; always answers with exactly one "result" event. */
  async command(cmd: Command): Promise<CoreEvent> {
    const id = typeof cmd.id === "number" ? cmd.id : this.nextAutoId++;
    try {
      const data = await this.dispatch(cmd);
      return this.event("result", data ? { id, ok: true, data } : { id, ok: true });
    } catch (e) {
      const code = e instanceof CommandError ? e.code : "failed";
      const message = e instanceof Error ? e.message : String(e);
      return this.event("result", { id, ok: false, error: code, message });
    }
  }

  // ------------------------------------------------------------------
  private async dispatch(c: Command): Promise<Json | void> {
    switch (c.cmd) {
      case "core.dnd":
        this.dnd = c.enable !== false;
        return;
      case "account.add":
        return this.accountAdd(c.account as AccountConfig);
      case "account.remove":
        return this.accountRemove(str(c.account));
      case "account.register":
        this.account(str(c.account)).stack.register();
        return;
      case "account.unregister":
        await this.account(str(c.account)).stack.unregister();
        return;
      case "account.list":
        return {
          accounts: [...this.accounts.values()].map((a) => ({
            id: a.cfg.id,
            aor: a.cfg.uri,
            display_name: a.cfg.display_name ?? "",
            line: a.cfg.line ?? 0,
            caller_id: a.cfg.caller_id ?? "",
            registered: a.registered,
            last: a.last,
          })),
        };
      case "call.dial": {
        if (c.ref !== undefined && !(typeof c.ref === "string" && REF.test(c.ref))) throw new CommandError("invalid", "ref");
        return this.dial(str(c.account), str(c.to), c.ref as string | undefined);
      }
      case "call.answer":
        return this.answer(this.call(c));
      case "call.hangup": {
        const code = c.code === undefined ? undefined : Number(c.code);
        if (code !== undefined && (code < 400 || code > 699)) throw new CommandError("invalid");
        await this.call(c).stack.hangup(code);
        return;
      }
      case "call.hold":
        return this.setHold(this.call(c), c.hold !== false, true);
      case "call.mute": {
        const rec = this.call(c);
        rec.muted = c.mute !== false;
        rec.stack.setMicEnabled(!rec.muted && !rec.silenced);
        return;
      }
      case "call.dtmf": {
        const digits = str(c.digits);
        if (!DIGITS.test(digits)) throw new CommandError("invalid");
        this.call(c).stack.sendDtmf(digits);
        return;
      }
      case "call.transfer": {
        const rec = this.call(c);
        const to = str(c.to);
        if (!to || UNSAFE.test(to)) throw new CommandError("invalid");
        await rec.stack.refer(this.account(rec.account).stack.complete(to));
        return;
      }
      case "call.list":
        return { calls: [...this.calls.values()].map((r) => this.info(r)) };
      case "conference.merge":
        return this.conference.merge(c.calls as string[] | undefined);
      case "conference.add":
        return this.conference.add(this.call(c));
      case "conference.split":
        return this.conference.split(this.call(c));
      case "conference.end":
        return this.conference.end();
      case "conference.list":
        return { calls: this.conference.members() };
      case "diag.mos":
        return mosEstimate(c as unknown as MosInput);
      // web additions: provisioning hands out short-lived credentials
      case "account.credentials": {
        const rec = this.account(str(c.account));
        const ha1 = c.ha1 === undefined ? undefined : str(c.ha1);
        const password = c.password === undefined ? undefined : str(c.password);
        if (!str(c.auth_user) || !!ha1 === !!password || (ha1 && !/^[0-9a-f]{32}$/i.test(ha1))) {
          throw new CommandError("invalid");
        }
        rec.stack.setCredentials(str(c.auth_user), { ha1, password });
        return;
      }
      case "core.ice_servers": {
        if (!Array.isArray(c.ice_servers)) throw new CommandError("invalid");
        this.stack.setIceServers?.(c.ice_servers as RTCIceServer[]);
        return;
      }
      default: {
        for (const m of this.modules) {
          const r = m.command(c);
          if (r !== undefined) return r;
        }
        throw new CommandError("unknown_command", `unknown command ${c.cmd}`);
      }
    }
  }

  // --- accounts -------------------------------------------------------
  private accountAdd(cfg: AccountConfig): void {
    if (!cfg || typeof cfg !== "object" || !cfg.id || !cfg.uri) {
      throw new CommandError("invalid");
    }
    if (!/^sips?:[^@<>"\s]+@[^<>"\s]+$/.test(cfg.uri)) throw new CommandError("invalid");
    if (cfg.ha1 && cfg.password) throw new CommandError("invalid");
    if (cfg.ha1 && !/^[0-9a-f]{32}$/i.test(cfg.ha1)) throw new CommandError("invalid");
    if (this.accounts.has(cfg.id)) throw new CommandError("exists");

    const rec: AccountRec = { cfg, registered: false } as AccountRec;
    rec.stack = this.stack.addAccount(cfg, {
      onRegState: (state: RegState, reason?: string) => {
        rec.registered = state === "registered";
        if (reason) rec.last = reason;
        if (state === "unregistered") return;
        this.event("account.state", { account: cfg.id, state, ...(reason ? { reason } : {}) });
      },
      onIncoming: (call: StackCall) => this.incoming(cfg.id, call),
    });
    this.accounts.set(cfg.id, rec);
    if (cfg.register !== false) rec.stack.register();
  }

  private async accountRemove(id: string): Promise<void> {
    const rec = this.account(id);
    this.accounts.delete(id);
    await rec.stack.dispose();
    this.event("account.removed", { account: id });
  }

  // --- calls ----------------------------------------------------------
  private dial(accountId: string, to: string, ref?: string): Json {
    const acc = this.account(accountId);
    if (!to || UNSAFE.test(to)) throw new CommandError("invalid");
    const target = acc.stack.complete(to);
    let rec: CallRec | undefined;
    const listener = this.listener(() => rec!);
    const call = acc.stack.dial(target, listener, ref ? [`${REF_HEADER}: ${ref.trim()}`] : undefined);
    rec = this.track(accountId, call, "out", "outgoing");
    if (ref) rec.ref = ref;
    this.focus(rec);
    this.holdOthers(rec);
    this.emitCall(rec, "outgoing");
    return { call: rec.id };
  }

  private incoming(accountId: string, call: StackCall): void {
    if (this.dnd) {
      void call.hangup(486);
      this.log("info", `do not disturb: rejected call from ${call.peer}`);
      return;
    }
    let rec: CallRec | undefined;
    call.listen(this.listener(() => rec!));
    rec = this.track(accountId, call, "in", "incoming");
    this.emitCall(rec, "incoming");
  }

  private async answer(rec: CallRec): Promise<void> {
    if (rec.state !== "incoming") throw new CommandError("invalid", "not ringing");
    this.unsilence(rec);
    this.focus(rec);
    this.holdOthers(rec);
    await rec.stack.answer();
  }

  private track(account: string, stack: StackCall, dir: "in" | "out", state: CallState): CallRec {
    const rec: CallRec = {
      id: stack.id,
      account,
      stack,
      dir,
      state,
      onHold: false,
      wantHold: false,
      holdBusy: false,
      remoteHold: false,
      silenced: false,
      muted: false,
    };
    this.calls.set(rec.id, rec);
    return rec;
  }

  private listener(get: () => CallRec): CallListener {
    return {
      onState: (s: StackCallState, reason?: string, code?: number) => this.onState(get(), s, reason, code),
      onRemoteHold: (hold: boolean) => {
        const rec = get();
        rec.remoteHold = hold;
        this.event("call.remote_hold", { call: rec.id, hold: hold ? "true" : "false" });
      },
      onReferred: (to: string) => void this.followTransfer(get(), to),
    };
  }

  private onState(rec: CallRec, s: StackCallState, reason?: string, code?: number): void {
    if (rec.state === "ended") return;
    rec.state = s;
    if (s === "established" && !rec.started) rec.started = Date.now();
    this.emitCall(rec, s, reason, code);

    if (s === "established") {
      this.onEstablished(rec);
    } else if (s === "ended") {
      this.calls.delete(rec.id);
      if (this.focusId === rec.id) this.focusId = "";
      this.conference.removed(rec);
    }
  }

  /* A call connected while another one has the focus: hold it at once. */
  private onEstablished(rec: CallRec): void {
    if (rec.wantHold !== rec.onHold) void this.reconcileHold(rec);
    if (!this.focusId || this.focusId === rec.id) return;
    const focus = this.calls.get(this.focusId);
    if (!focus || focus.state === "ended" || focus.wantHold) return;
    if (this.conference.together(focus, rec)) return;
    this.log("info", `${rec.id} connected while ${focus.id} has the focus: holding it`);
    this.silence(rec);
    void this.setHold(rec, true, false);
  }

  private focus(rec: CallRec): void {
    this.focusId = rec.id;
  }

  /* One active call: the others go on hold (connected) or quiet (not yet). */
  holdOthers(keep: CallRec): void {
    for (const r of this.calls.values()) {
      if (r === keep || r.state === "ended" || this.conference.together(keep, r)) continue;
      if (r.state === "established") {
        if (!r.wantHold) {
          this.silence(r);
          void this.setHold(r, true, false);
        }
      } else if (r.state !== "incoming") {
        this.silence(r);
      }
    }
  }

  async setHold(rec: CallRec, hold: boolean, user: boolean): Promise<void> {
    if (!hold) {
      this.unsilence(rec);
      if (user) {
        this.focus(rec);
        this.holdOthers(rec);
      }
    }
    rec.wantHold = hold;
    if (rec.state === "established") await this.reconcileHold(rec);
  }

  /* The latest wish wins, applied one re-INVITE at a time. */
  private async reconcileHold(rec: CallRec): Promise<void> {
    if (rec.holdBusy) return;
    rec.holdBusy = true;
    try {
      while (rec.state === "established" && rec.wantHold !== rec.onHold) {
        const target = rec.wantHold;
        try {
          await rec.stack.setHold(target);
          rec.onHold = target;
        } catch (e) {
          this.log("warn", `hold ${target} failed on ${rec.id}: ${String(e)}`);
          break;
        }
        this.emitCall(rec, rec.state);
      }
    } finally {
      rec.holdBusy = false;
    }
  }

  silence(rec: CallRec): void {
    rec.silenced = true;
    rec.stack.setMicEnabled(false);
    rec.stack.setPlayback(false);
  }

  unsilence(rec: CallRec): void {
    if (!rec.silenced) return;
    rec.silenced = false;
    rec.stack.setMicEnabled(!rec.muted);
    rec.stack.setPlayback(true);
  }

  private async followTransfer(rec: CallRec, to: string): Promise<void> {
    try {
      const res = this.dial(rec.account, to, rec.ref);
      const nc = this.calls.get(str(res.call));
      if (nc) nc.transferredFrom = rec.id;
      this.event("call.transferred", { call: rec.id, to, ok: true, new_call: res.call });
    } catch (e) {
      this.event("call.transferred", { call: rec.id, to, ok: false });
      this.log("warn", `transfer to ${to} failed: ${String(e)}`);
    }
  }

  // --- stats ------------------------------------------------------------
  private async emitStats(): Promise<void> {
    for (const rec of this.calls.values()) {
      if (rec.state !== "established") continue;
      const pc = rec.stack.peerConnection();
      if (!pc) continue;
      const s = await callStats(rec.id, pc).catch(() => undefined);
      if (!s) continue;
      this.event("call.stats", {
        call: rec.id,
        duration: this.duration(rec),
        ...s,
      });
    }
  }

  // --- helpers ------------------------------------------------------------
  info(r: CallRec): CallInfo {
    return {
      call: r.id,
      account: r.account,
      state: r.state,
      dir: r.dir,
      peer: r.stack.peer,
      ...(r.stack.peerName ? { peer_name: r.stack.peerName } : {}),
      ...(r.ref ? { ref: r.ref } : {}),
      ...(r.stack.headers ? { headers: r.stack.headers } : {}),
      on_hold: r.onHold,
      silenced: r.silenced,
      muted: r.muted,
      conference: this.conference.has(r),
      duration: this.duration(r),
    };
  }

  private duration(r: CallRec): number {
    return r.started ? Math.floor((Date.now() - r.started) / 1000) : 0;
  }

  private emitCall(rec: CallRec, state: CallState, reason?: string, code?: number): void {
    this.event("call.state", {
      ...this.info(rec),
      state,
      ...(reason ? { reason } : {}),
      ...(state === "ended" && code ? { code } : {}),
    });
  }

  callRec(id: string): CallRec | undefined {
    return this.calls.get(id);
  }

  allCalls(): CallRec[] {
    return [...this.calls.values()];
  }

  private call(c: Command): CallRec {
    const rec = this.calls.get(str(c.call));
    if (!rec) throw new CommandError("no_call");
    return rec;
  }

  private account(id: string): AccountRec {
    const rec = this.accounts.get(id);
    if (!rec) throw new CommandError("no_account");
    return rec;
  }

  event(name: string, fields: Json = {}): CoreEvent {
    const ev = { ev: name, ...fields } as CoreEvent;
    this.emit(ev);
    return ev;
  }

  log(level: string, msg: string): void {
    this.event("log", { level, msg });
  }
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}
